import type { UserMessageOptions } from "@mirage/service-conversation/types";
import { useCallback, useState } from "react";

interface StoredConversationState {
  userMessageOptions?: UserMessageOptions; // includes clientContext, searchParams, agentIds, etc.
}

interface ResetConversationStateProps {
  conversationId?: string;
  setUserMessageOptions: (options: UserMessageOptions) => void;
}

/**
 * Conversation types that support reset functionality.
 * These are conversation types that set initial state but don't automatically send messages.
 *
 * The stored state includes:
 * - userMessageOptions
 *   - clientContext
 *   - searchParams
 *   - agentIds
 *   - fssSearchScope
 */
export const useResetConversationState = ({
  conversationId,
  setUserMessageOptions,
}: ResetConversationStateProps) => {
  // stores the state needed to reset the conversation state back to the initial start
  // set* call should only be called if the initial state is something we want to store.
  // cases that automatically initiate a new message do not qualify.

  // Track stored state for current conversation only
  const [storedState, setStoredState] = useState<StoredConversationState>({});

  const storeInitialState = useCallback(
    (state: Partial<StoredConversationState>) => {
      if (!conversationId) {
        return;
      }

      // Store new initial state
      setStoredState({ ...(state || {}) });
    },
    [conversationId],
  );

  const resetToInitialState = useCallback(() => {
    // Need a conversation ID to find stored state
    if (!conversationId) {
      return;
    }

    // Reset each piece of state to its stored initial value
    if (storedState.userMessageOptions !== undefined) {
      setUserMessageOptions(storedState.userMessageOptions);
    }
  }, [conversationId, storedState, setUserMessageOptions]);

  return {
    storeState: storeInitialState,
    resetToInitialState,
  };
};
